import { Button, Card, CardContent, View } from "@/components/ui";
import { ListHeading } from "@/components/shared/list-header";
import { CalendarPlus } from "@/components/shared/icons";
import { TimeSlotWithSearch } from "@/components/shared/time-slot-with-search";
import type { TimeSlot } from "@/types/booking";
import { PackageDepartmentEnum } from "@/__generated__/graphql";
import type { ServiceName } from "@/types/service";
import { Typography } from "@/components/shared/typography";
import { generateBookingChatSalesMessage, onChatSales } from "@/lib/chat-sales";
import { PastMassageTherapistWorkerSection } from "../past-massage-therapist-worker";

export const SelectSlotSection = ({
  name,
  loading,
  showTimeRange,
  timeSlots,
  selectedSlot,
  startDate,
  endDate,
  department,
  setStartDate,
  setEndDate,
  workerIds,
  workerLabel,
  initialStartDate,
  enableChooseWorker,
  onChooseWorkers,
  setSelectedSlot,
  onClickWorkerPortfolio,
}: {
  name: ServiceName;
  loading: boolean;
  showTimeRange?: boolean;
  selectedSlot: TimeSlot | null;
  timeSlots: Record<string, TimeSlot[]> | null;
  startDate: Date;
  endDate: Date;
  initialStartDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  workerIds: string[];
  workerLabel: string;
  enableChooseWorker?: boolean;
  department: PackageDepartmentEnum;
  onChooseWorkers(): void;
  setSelectedSlot(p: TimeSlot): void;
  onClickWorkerPortfolio(slot: TimeSlot): void;
}) => {
  return (
    <View>
      <View className="flex flex-col gap-4 px-4">
        <View>
          <ListHeading title="Select Slot" icon={CalendarPlus} />
          <Typography color="foreground-intermediate" variant="body-sm">
            Choose the slot that works best for your schedule.
          </Typography>
        </View>

        {department === PackageDepartmentEnum.Massage && (
          <PastMassageTherapistWorkerSection />
        )}

        <TimeSlotWithSearch
          name={name}
          initialStartDate={initialStartDate}
          department={department}
          showTimeRange={showTimeRange}
          loading={loading}
          selectedSlot={selectedSlot}
          timeSlots={timeSlots}
          workersIds={workerIds}
          workerLabel={workerLabel}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          enableChooseWorker={enableChooseWorker}
          onChooseWorkers={onChooseWorkers}
          onSelectSlot={setSelectedSlot}
          onClickWorkerPortfolio={onClickWorkerPortfolio}
        />
      </View>
      <SelectSlotChatSales department={department} name={name} />
    </View>
  );
};

const SelectSlotChatSales = ({
  department,
  name,
}: {
  department: PackageDepartmentEnum;
  name: ServiceName;
}) => {
  const tilte =
    department === PackageDepartmentEnum.DeepCleaning
      ? "Can't find what you need?"
      : "Didn’t find a time that works?";

  const bodyText =
    department === PackageDepartmentEnum.DeepCleaning
      ? `Our schedule shows available slots starting two days ahead, but chat with our team, we’ll do our best to help!`
      : "Chat with our sales team, we’ll help you book a slot that fits your schedule.";

  return (
    <Card className="mx-4 my-2 cursor-default">
      <CardContent>
        <View className="flex flex-row items-center">
          <View className="item-start flex w-full flex-1 flex-col justify-between gap-4 md:flex-row md:items-center">
            <View className="flex max-w-[344px] flex-col gap-1 md:gap-2">
              <Typography variant="label-sm">{tilte}</Typography>
              <Typography variant="body-sm" color="foreground-intermediate">
                {bodyText}
              </Typography>
            </View>
            <Button
              variant="primary"
              color="success"
              size="sm"
              className="md:max-w-[146px]"
              iconName="whatsappLogo"
              iconColor="fill-white"
              onClick={() => {
                onChatSales(true, generateBookingChatSalesMessage(name));
              }}
              children="Chat with Sales"
            />
          </View>
        </View>
      </CardContent>
    </Card>
  );
};
